import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Box,
  InputAdornment,
} from "@mui/material";
import * as Yup from "yup";
import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useState, useRef } from "react";
import {
  getMerchantDetails,
  getActiveBusinessEntity,
  merchantAction,
  Log,
  sendDataWithPost,
  getCreateAccOptions,
} from "../../../services/services";
import { LoadingButton } from "@mui/lab";
import { isNumericInput, scrollToFieldError, trimValues } from "../../../utils/appUtils";
import FormDialog from "../../FormDialog";
import Select from "react-select";
import { DEFAULT_MIN_PAYOUT } from "../../../utils/constants";
// import { MAPPED_REGISTERED_COUNTRY } from "../../../utils/constants";

const FormErrorScroll = () => {
  const { isValid, submitCount, errors } = useFormikContext();

  useEffect(() => {
    if (isValid || submitCount === 0) return;
    scrollToFieldError(errors);
  }, [submitCount, isValid]);

  return null;
};

const EditDialogs = ({
  open,
  onClose,
  selectedId,
  setResponse,
  setTableLoaded,
}) => {
  const mountedRef = useRef(true);
  const [optLoaded, setOptLoaded] = useState(false);
  const [options, setOptions] = useState();
  const [loaded, setLoaded] = useState(false);
  const [merchant, setMerchant] = useState();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  const [staticOptions, setStaticOptions] = useState("");
  const [staticOptLoaded, setStaticOptLoaded] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    setLoaded(false);
    setMerchant();
    setOptions();
    setOptLoaded(false);
    setStaticOptLoaded(false);
    setBankAccounts([]);
    setBankAccountsLoaded(false);
    getMerchantDetails(selectedId, res => {
      if (mountedRef.current) {
        setMerchant({ ...res, name: res.name?.toUpperCase() })
        getBankAccounts(res?.business_entity?.id);
      }
    }, () => { if (mountedRef.current) setLoaded(true) });
    getActiveBusinessEntity(res => { if (mountedRef.current) setOptions(res) }, () => { if (mountedRef.current) setOptLoaded(true) });
    getCreateAccOptions(res => { if (mountedRef.current) setStaticOptions(res) }, () => { if (mountedRef.current) setStaticOptLoaded(true) });
    return () => {
      mountedRef.current = false;
    };
  }, [selectedId]);

  const setDataBankAccounts = (res) => {
    if (mountedRef.current) {
      if (!res?.bank_accounts) {
        setBankAccounts([]);
        return;
      };
      setBankAccounts(res?.bank_accounts?.map((item) => item));
    }
  }

  const getBankAccounts = async (business_entity_id) => {
    const url = `/merchant/merchant/bank_accounts_by_business_entity/`;
    sendDataWithPost(url, { business_entity_id }, setDataBankAccounts, () => { if (mountedRef.current) setBankAccountsLoaded(true) });
  }

  return optLoaded && loaded ? (
    <Formik
      initialValues={{
        name: merchant.name ?? "",
        business_entity: merchant.business_entity
          ? {
            id: merchant.business_entity?.id,
            label: merchant.business_entity?.business_name
          }
          : "",
        bank_account: merchant.bank_account
          ? {
            id: merchant.bank_account?.id,
            label: `${merchant.bank_account?.account_number} (${merchant.bank_account?.bank})`
          }
          : "",
        mid: merchant.mid ?? "",
        can_void: merchant.can_void ? merchant.can_void : false,
        can_refund: merchant.can_refund ? merchant.can_refund : false,
        can_merchant_void: merchant.can_merchant_void ? merchant.can_merchant_void : false,
        can_merchant_refund: merchant.can_merchant_refund ? merchant.can_merchant_refund : false,
        // registered_country: merchant?.registered_country?.id,
        merchant_category_code: merchant?.mcc
          ? {
            id: merchant.mcc?.id,
            label: `${merchant.mcc?.code} - ${merchant.mcc?.description}`,
          }
          : "",
        oxygen7_entity: merchant.oxygen7_entity
          ? {
            id: merchant.oxygen7_entity?.id,
            label: merchant.oxygen7_entity?.entity
          }
          : "",
        address_line1: merchant?.address_line1 ?? "",
        address_line2: merchant?.address_line2 ?? "",
        address_line3: merchant?.address_line3 ?? "",
        // address_line4: merchant?.address_line4 ?? "",
        // address_line5: merchant?.address_line5 ?? "",
        city: merchant?.city ?? "",
        postal_code: merchant?.postal_code ?? "",
        address_as_business_entity: false,
        min_payout_amount: (() => {
          if (!merchant?.min_payout_amount || !Array.isArray(merchant.min_payout_amount)) {
            return [];
          }
          return merchant.min_payout_amount;
        })(),
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Required field").trim(),
        business_entity: Yup.object().required("Required field"),
        oxygen7_entity: Yup.object().required("Required field"),
        // registered_country: Yup.string().required("Required field"),
        merchant_category_code: Yup.object().required("Required field"),
        bank_account: Yup.object().nullable().required("Required field"),
        address_line1: Yup.string().when("address_as_business_entity", {
          is: false,
          then: Yup.string().required("Required field"),
        }).trim(),
        min_payout_amount: Yup.array().of(
          Yup.object().shape({
            min_amount: Yup
              .number()
              .typeError("Must be a number")
              .positive("Must be a positive number")
              .max(999999999999, "Must be smaller than 999,999,999,999")
              .required("Required field"),
          })
        ),
      })}
      onSubmit={async (values) => {
        const newValues = trimValues(values);
        if (newValues.min_payout_amount && newValues.min_payout_amount.length === 0) {
          newValues.min_payout_amount = null;
        }
        Log.info(
          `Click button [Submit] - Update Merchant details by Id: ${selectedId}`,
          newValues
        );
        await merchantAction("update", {
          data: {
            ...newValues,
            pk: selectedId,
            merchant_category_code: newValues.merchant_category_code.id,
            business_entity: newValues.business_entity.id,
            bank_account: newValues.bank_account.id,
            oxygen7_entity: newValues.oxygen7_entity.id
          },
          cb: onClose,
        });
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        } = props;
        return (
          <FormDialog
            open={open}
            onClose={onClose}
            title="Edit Merchant"
            handleSubmit={handleSubmit}
            loading={isSubmitting}
            maxWidth="lg"
          >
            <Form autoComplete="off" noValidate>
              <FormErrorScroll />
              <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
                <Grid item sm={6} xs={12}>
                  <Typography variant="h6" my={1}>
                    Merchant Information
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right" mb={1}>MID</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <Typography marginBottom={1}>{values.mid}</Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Merchant Name*</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <TextField
                        label="Merchant Name"
                        placeholder="Merchant Name"
                        name="name"
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        color="secondary"
                        value={values.name}
                        onChange={(e) => {
                          const uppercasedValue = e.target.value.toUpperCase();
                          setFieldValue("name", uppercasedValue)
                        }}
                        onBlur={handleBlur}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Business Entity*</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <Autocomplete
                        options={options?.business_entity?.map((entity) => ({
                          id: entity.id,
                          label: entity.business_name,
                        })) || []}
                        value={values.business_entity}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        onChange={(e, newValue) => {
                          setFieldValue("business_entity", newValue ?? "");
                          getBankAccounts(newValue?.id);
                          setFieldValue("bank_account", "");
                        }}
                        fullWidth
                        size="small"
                        disabled={isSubmitting}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Business Entity"
                            placeholder="Business Entity"
                            margin="dense"
                            color="secondary"
                            error={Boolean(touched.business_entity && errors.business_entity)}
                            helperText={touched.business_entity && errors.business_entity}
                            name="business_entity"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Bank Account*</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <Autocomplete
                        options={bankAccounts?.map((item) => ({
                          id: item.id,
                          label: `${item.account_number} (${item.bank})`,
                        })) || []}
                        value={values.bank_account}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        onChange={(e, newValue) => {
                          setFieldValue("bank_account", newValue ?? "");
                        }}
                        fullWidth
                        size="small"
                        disabled={isSubmitting}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Bank Account"
                            placeholder="Bank Account"
                            margin="dense"
                            color="secondary"
                            error={Boolean(touched.bank_account && errors.bank_account)}
                            helperText={touched.bank_account && errors.bank_account}
                            name="bank_account"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                  </Grid>

                  {/* <Grid item md={4} xs={4}>
                    <Typography variant="subtitle1" sx={{ textAlign: "right" }}>
                      Registered Country*
                    </Typography>
                  </Grid>
                  <Grid item md={8} xs={8}>
                    <TextField
                      margin="normal"
                      required
                      select
                      fullWidth
                      id="registered_country"
                      name="registered_country"
                      size="small"
                      color="secondary"
                      value={values.registered_country}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.registered_country && errors.registered_country
                      )}
                      helperText={
                        touched.registered_country && errors.registered_country
                      }
                      sx={{ minWidth: "50%" }}
                      disabled={isSubmitting}
                    >
                      {options?.registered_country?.map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {MAPPED_REGISTERED_COUNTRY[country.registered_country]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid> */}

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Merchant Category Code*</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <Autocomplete
                        options={options?.mcc?.map((mcc) => ({
                          id: mcc.id,
                          label: mcc.code_description,
                        })) || []}
                        value={values.merchant_category_code}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        onChange={(e, newValue) => {
                          setFieldValue("merchant_category_code", newValue ?? "");
                        }}
                        fullWidth
                        size="small"
                        disabled={isSubmitting}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Merchant Category Code"
                            placeholder="Merchant Category Code"
                            margin="dense"
                            color="secondary"
                            error={Boolean(touched.merchant_category_code && errors.merchant_category_code)}
                            helperText={touched.merchant_category_code && errors.merchant_category_code}
                            name="merchant_category_code"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Oxygen7 Entity*</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <Autocomplete
                        options={(staticOptions?.oxygen7_entity || [])
                          ?.filter(
                            (entity) =>
                              entity?.entity !== "OXYGEN7_IND" &&
                              entity?.entity !== "OXYGEN7_HKG"
                          )
                          .map((entity) => ({
                            id: entity.id,
                            label: entity.entity,
                          })) || []}
                        value={values.oxygen7_entity}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        onChange={(e, newValue) => {
                          setFieldValue("oxygen7_entity", newValue ?? "");
                        }}
                        fullWidth
                        size="small"
                        disabled={isSubmitting}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Oxygen7 Entity"
                            placeholder="Oxygen7 Entity"
                            margin="dense"
                            color="secondary"
                            error={Boolean(touched.oxygen7_entity && errors.oxygen7_entity)}
                            helperText={touched.oxygen7_entity && errors.oxygen7_entity}
                            name="oxygen7_entity"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <Typography variant="h6" my={1}>
                    Payment Settings
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="can_void"
                          checked={values.can_void}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Can Void"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="can_refund"
                          checked={values.can_refund}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Can Refund"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="can_merchant_void"
                          checked={values.can_merchant_void}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Can Void via Merchant Portal"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="can_merchant_refund"
                          checked={values.can_merchant_refund}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Can Refund via Merchant Portal"
                    />
                  </Box>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <Typography variant="h6" my={1}>
                    Address Details
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Address 1{values.address_as_business_entity ? "" : "*"}</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <TextField
                        label="Address 1"
                        placeholder="Address 1"
                        name="address_line1"
                        fullWidth
                        margin="dense"
                        size="small"
                        color="secondary"
                        value={values.address_line1}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.address_line1 && errors.address_line1)}
                        helperText={touched.address_line1 && errors.address_line1}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Address 2</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <TextField
                        label="Address 2"
                        placeholder="Address 2"
                        name="address_line2"
                        fullWidth
                        margin="dense"
                        size="small"
                        color="secondary"
                        value={values.address_line2}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Address 3</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <TextField
                        label="Address 3"
                        placeholder="Address 3"
                        name="address_line3"
                        fullWidth
                        margin="dense"
                        size="small"
                        color="secondary"
                        value={values.address_line3}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>

                {/* <Grid item md={4} xs={4}>
                    <Typography variant="subtitle1" sx={{ textAlign: "right" }}>
                      Address line 4
                    </Typography>
                  </Grid>
                  <Grid item md={8} xs={8}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="address_line4"
                      name="address_line4"
                      label="Address line 4"
                      size="small"
                      color="secondary"
                      value={values.address_line4}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.address_line4 && errors.address_line4
                      )}
                      helperText={touched.address_line4 && errors.address_line4}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item md={4} xs={4}>
                    <Typography variant="subtitle1" sx={{ textAlign: "right" }}>
                      Address line 5
                    </Typography>
                  </Grid>
                  <Grid item md={8} xs={8}>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="address_line5"
                      name="address_line5"
                      label="Address line 5"
                      size="small"
                      color="secondary"
                      value={values.address_line5}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.address_line5 && errors.address_line5
                      )}
                      helperText={touched.address_line5 && errors.address_line5}
                      disabled={isSubmitting}
                    />
                  </Grid> */}

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">City</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <TextField
                        label="City"
                        placeholder="City"
                        name="city"
                        fullWidth
                        margin="dense"
                        size="small"
                        color="secondary"
                        value={values.city}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={4} xs={4}>
                      <Typography align="right">Postal Code</Typography>
                    </Grid>
                    <Grid item md={8} xs={8}>
                      <TextField
                        label="Postal Code"
                        placeholder="Postal Code"
                        name="postal_code"
                        fullWidth
                        size="small"
                        margin="dense"
                        color="secondary"
                        value={values.postal_code}
                        onChange={e => {
                          if (isNumericInput(e.target.value)) {
                            handleChange(e);
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ pl: 2, mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="address_as_business_entity"
                          checked={values.address_as_business_entity}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Address as Business Entity"
                    />
                  </Box>
                </Grid>

                <Grid item sm={6} xs={12}>
                  <Typography variant="h6" my={1}>
                    Minimum Payout Amount
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Select
                      id="currency_list"
                      name="currency_list"
                      getOptionLabel={option => option.currency}
                      getOptionValue={option => option.id}
                      closeMenuOnSelect={false}
                      isMulti
                      options={DEFAULT_MIN_PAYOUT.map(c => ({ id: c.currency, currency: c.currency }))}
                      value={(values.min_payout_amount || []).map(c => ({ id: c.currency, currency: c.currency }))}
                      onChange={selected => {
                        const selectedArray = selected || [];
                        const newConfig = selectedArray.map(option => {
                          const existing = (values.min_payout_amount || []).find(c => c.currency === option.id);
                          if (existing) return existing;
                          
                          const defaultAmt = DEFAULT_MIN_PAYOUT.find(d => d.currency === option.id)?.min_amount || "";
                          return { currency: option.id, min_amount: defaultAmt };
                        });
                        setFieldValue('min_payout_amount', newConfig);
                      }}
                      styles={{
                        control: provided => ({ ...provided, minHeight: "40px", fontSize: "16px" }),
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        placeholder: provided => ({ ...provided, fontSize: "16px" }),
                        singleValue: provided => ({ ...provided, fontSize: "16px" }),
                        option: provided => ({ ...provided, fontSize: "16px" })
                      }}
                      menuPortalTarget={document.body}
                    />

                    {values.min_payout_amount?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {(values.min_payout_amount || []).map((item, index) => {
                          const fieldName = `min_payout_amount[${index}].min_amount`;
                          const fieldError = errors?.min_payout_amount?.[index]?.min_amount;
                          const fieldTouched = touched?.min_payout_amount?.[index]?.min_amount;
                          const minAmtError = Boolean(fieldTouched && fieldError);

                          return (
                            <TextField
                              key={item.currency}
                              sx={{ m: 1 }}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {item.currency}
                                  </InputAdornment>
                                ),
                              }}
                              label="Min Amount"
                              id={fieldName}
                              name={fieldName}
                              value={item.min_amount}
                              onChange={(e) => {
                                const newConfig = [...(values.min_payout_amount || [])];
                                newConfig[index] = { ...newConfig[index], min_amount: e.target.value === '' ? '' : parseFloat(e.target.value) };
                                setFieldValue('min_payout_amount', newConfig);
                              }}
                              type="number"
                              inputProps={{ min: 0, step: 0.01 }}
                              error={minAmtError}
                              helperText={minAmtError ? fieldError : ""}
                              variant="standard"
                              disabled={isSubmitting}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormDialog>
        );
      }}
    </Formik>
  ) : (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

export default EditDialogs;
